export const METRIC_GROUPS = {
  patrimoine: 'Patrimoine',
  revenus: 'Revenus',
  cashflow: 'Cashflow',
  fiscal: 'Fiscal',
  performance: 'Performance',
};

export const SCENARIO_COLORS = {
  A: '#3b82f6',
  B: '#f59e0b',
};

export function getPlacementsTotal(row) {
  if (row?.placementsTotal != null) return row.placementsTotal;
  const immo = row?.immobilierTotal || 0;
  const assurance = row?.assuranceParticipativeTotal || 0;
  return Math.max(0, (row?.totalAssets || 0) - immo - assurance);
}

export function getImmoPct(row) {
  if (!row?.totalAssets) return 0;
  return ((row.immobilierTotal || 0) / row.totalAssets) * 100;
}

export function getTaxablePct(row) {
  if (!row?.totalAssets) return 0;
  return ((row.taxableAtSaleTotal || 0) / row.totalAssets) * 100;
}

export function computeCagr(firstRow, lastRow) {
  if (!firstRow || !lastRow) return 0;
  const start = firstRow.netWorth || 0;
  const end = lastRow.netWorth || 0;
  const years = Math.max(1, (lastRow.year || 0) - (firstRow.year || 0));
  if (start <= 0 || end <= 0) return 0;
  return (Math.pow(end / start, 1 / years) - 1) * 100;
}

const BASE_METRICS = [
  {
    id: 'netWorth',
    label: 'Valeur nette',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.netWorth ?? 0,
  },
  {
    id: 'totalAssets',
    label: 'Actifs totaux',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.totalAssets ?? 0,
  },
  {
    id: 'totalDebts',
    label: 'Dettes totales',
    group: 'patrimoine',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.totalDebts ?? 0,
  },
  {
    id: 'immobilierTotal',
    label: 'Valeur immeubles',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.immobilierTotal ?? 0,
  },
  {
    id: 'placementsTotal',
    label: 'Placements et comptes (hors assurance)',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => getPlacementsTotal(row),
  },
  {
    id: 'assuranceParticipativeTotal',
    label: 'Assurance participative (valeur de rachat)',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.assuranceParticipativeTotal ?? 0,
  },
  {
    id: 'assuranceDeathBenefitTotal',
    label: 'Assurance participative (capital décès)',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.assuranceDeathBenefitTotal ?? 0,
  },
  {
    id: 'collateralCapacityTotal',
    label: 'Capacité collatérale disponible',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.collateralCapacityTotal ?? 0,
  },
  {
    id: 'collateralLoanBalance',
    label: 'Encours prêts sur polices',
    group: 'patrimoine',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.collateralLoanBalance ?? 0,
  },
  {
    id: 'pfiLoanBalance',
    label: 'Encours PFI',
    group: 'patrimoine',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.pfiLoanBalance ?? 0,
  },
  {
    id: 'pfiAdvanceThisYear',
    label: 'Avance PFI (année)',
    group: 'cashflow',
    higherIsBetter: null,
    format: 'currency',
    getValue: (row) => row?.pfiAdvanceThisYear ?? 0,
  },
  {
    id: 'annualCollateralInterest',
    label: 'Intérêts collatéral / PFI',
    group: 'cashflow',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.annualCollateralInterest ?? 0,
  },
  {
    id: 'corpCdcBalance',
    label: 'Solde CDC (sociétés)',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.corpCdcBalance ?? 0,
  },
  {
    id: 'corpImrtdBalance',
    label: 'Solde IMRTD (sociétés)',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.corpImrtdBalance ?? 0,
  },
  {
    id: 'corpGripBalance',
    label: 'Solde GRIP (sociétés)',
    group: 'patrimoine',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.corpGripBalance ?? 0,
  },
  {
    id: 'dividendsPaidTotal',
    label: 'Dividendes versés (Inc.)',
    group: 'cashflow',
    higherIsBetter: null,
    format: 'currency',
    getValue: (row) => row?.dividendsPaidTotal ?? 0,
  },
  {
    id: 'dividendTaxPersonal',
    label: 'Impôt dividendes (personnel)',
    group: 'fiscal',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.dividendTaxPersonal ?? 0,
  },
  {
    id: 'immoPct',
    label: 'Exposition immobilière',
    group: 'patrimoine',
    higherIsBetter: null,
    format: 'percent',
    getValue: (row) => getImmoPct(row),
  },
  {
    id: 'taxablePct',
    label: 'Portion imposable',
    group: 'patrimoine',
    higherIsBetter: null,
    format: 'percent',
    getValue: (row) => getTaxablePct(row),
  },
  {
    id: 'annualRevenueGross',
    label: 'Revenus bruts',
    group: 'revenus',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.annualRevenueGross ?? row?.annualRevenue ?? 0,
  },
  {
    id: 'totalPersonalRevenueMonthly',
    label: 'Revenus personnels (mensuel)',
    group: 'revenus',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.totalPersonalRevenueMonthly ?? 0,
  },
  {
    id: 'annualRevenueAfterTax',
    label: 'Revenus après impôt',
    group: 'revenus',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.annualRevenueAfterTax ?? 0,
  },
  {
    id: 'cashflowImpactPersonnel',
    label: 'Impact cashflow (personnel)',
    group: 'cashflow',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.cashflowImpactPersonnel ?? 0,
  },
  {
    id: 'cashflowImpactCorporation',
    label: 'Impact cashflow (Inc.)',
    group: 'cashflow',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.cashflowImpactCorporation ?? 0,
  },
  {
    id: 'annualCashflow',
    label: 'Cashflow brut',
    group: 'cashflow',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => row?.annualCashflow ?? 0,
  },
  {
    id: 'annualDebtPayment',
    label: 'Service de la dette',
    group: 'cashflow',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.annualDebtPayment ?? 0,
  },
  {
    id: 'annualInsurancePremiums',
    label: "Primes d'assurance",
    group: 'cashflow',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.annualInsurancePremiums ?? 0,
  },
  {
    id: 'annualCashflowAfterTax',
    label: 'Cashflow net (somme actifs)',
    group: 'cashflow',
    higherIsBetter: true,
    format: 'currency',
    getValue: (row) => {
      if (row?.cashflowImpactTotal != null) return row.cashflowImpactTotal;
      return row?.annualCashflowAfterTax ?? 0;
    },
  },
  {
    id: 'annualTax',
    label: 'Impôt annuel estimé',
    group: 'fiscal',
    higherIsBetter: false,
    format: 'currency',
    getValue: (row) => row?.annualTax ?? 0,
  },
  {
    id: 'estateTax',
    label: 'Impôt successoral',
    group: 'fiscal',
    higherIsBetter: false,
    format: 'currency',
    fromBundle: true,
    getValue: (_row, bundle) => bundle?.deathSummary?.totalTaxAtDeath ?? 0,
  },
  {
    id: 'netEstate',
    label: 'Succession nette',
    group: 'fiscal',
    higherIsBetter: true,
    format: 'currency',
    fromBundle: true,
    getValue: (_row, bundle) => bundle?.deathSummary?.netEstate ?? 0,
  },
  {
    id: 'deathBenefit',
    label: 'Capital décès (assurance)',
    group: 'fiscal',
    higherIsBetter: true,
    format: 'currency',
    fromBundle: true,
    getValue: (_row, bundle) => bundle?.deathSummary?.deathBenefit ?? 0,
  },
  {
    id: 'cdcFromInsurance',
    label: 'Crédit CDC au décès (assurance corp.)',
    group: 'fiscal',
    higherIsBetter: true,
    format: 'currency',
    fromBundle: true,
    getValue: (_row, bundle) => bundle?.deathSummary?.cdcFromInsurance ?? 0,
  },
  {
    id: 'cagrNetWorth',
    label: 'CAGR valeur nette',
    group: 'performance',
    higherIsBetter: true,
    format: 'percent',
    needsSeries: true,
    getValue: (_row, bundle, ctx) => computeCagr(ctx?.firstRow, ctx?.lastRow),
  },
];

export const PRESET_METRICS = {
  essentiel: ['netWorth', 'annualCashflowAfterTax', 'immoPct', 'cagrNetWorth'],
  complet: [
    'netWorth', 'totalAssets', 'totalDebts', 'immobilierTotal', 'assuranceParticipativeTotal',
    'placementsTotal', 'annualRevenueGross', 'totalPersonalRevenueMonthly', 'annualCashflowAfterTax', 'annualTax', 'estateTax', 'netEstate', 'cagrNetWorth',
  ],
  fiscal: ['annualTax', 'annualRevenueAfterTax', 'estateTax', 'netEstate', 'deathBenefit', 'cdcFromInsurance', 'taxablePct'],
  assuranceCollateral: [
    'assuranceParticipativeTotal',
    'assuranceDeathBenefitTotal',
    'collateralCapacityTotal',
    'collateralLoanBalance',
    'pfiLoanBalance',
    'pfiAdvanceThisYear',
    'annualCollateralInterest',
    'netWorth',
  ],
  corporatifActionnaires: [
    'corpCdcBalance',
    'cdcFromInsurance',
    'corpImrtdBalance',
    'corpGripBalance',
    'dividendsPaidTotal',
    'dividendTaxPersonal',
    'netWorth',
  ],
};

export const DEFAULT_VISIBLE_METRICS = PRESET_METRICS.essentiel;

export function buildDynamicMetrics(bundle) {
  const assetNames = bundle?.assetNames || [];
  const revenueNames = bundle?.revenueNames || [];
  const dynamic = [];

  for (const name of assetNames) {
    dynamic.push({
      id: `asset::${name}`,
      label: name,
      group: 'patrimoine',
      higherIsBetter: true,
      format: 'currency',
      dynamic: true,
      getValue: (row) => row?.[name] ?? 0,
    });
  }

  for (const name of revenueNames) {
    dynamic.push({
      id: `rev::${name}`,
      label: name,
      group: 'revenus',
      higherIsBetter: true,
      format: 'currency',
      dynamic: true,
      getValue: (row) => row?.[`rev::${name}`] ?? 0,
    });
  }

  return dynamic;
}

export function getAllMetrics(bundle) {
  return [...BASE_METRICS, ...buildDynamicMetrics(bundle)];
}

export function getMetricById(id, bundle) {
  return getAllMetrics(bundle).find((m) => m.id === id);
}

export function resolveMetricValue(metric, row, bundle, ctx) {
  if (!metric) return 0;
  if (metric.fromBundle || metric.needsSeries) {
    return metric.getValue(row, bundle, ctx);
  }
  return metric.getValue(row, bundle, ctx);
}

export function formatMetricValue(value, format, formatCurrency) {
  if (value == null || Number.isNaN(value)) return '—';
  if (format === 'percent') return `${Number(value).toFixed(1)}%`;
  if (format === 'currency') return formatCurrency(value);
  return String(Math.round(value * 10) / 10);
}
